import { AssetsFile, BaseLoaderUtil, Words } from '../../types/asset/asset.types.ts';
import { KeyedWord } from '../../types/asset/word.types.ts';

import { AssetFilesUtil, getAssetFilesUtil } from './asset-files.util.ts';

export class WordsUtil implements BaseLoaderUtil {
  private static _instance: WordsUtil;
  private words: Words | null = null;
  private assetFilesUtil: AssetFilesUtil = getAssetFilesUtil();

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static get instance(): WordsUtil {
    if (!WordsUtil._instance) {
      WordsUtil._instance = new WordsUtil();
    }
    return WordsUtil._instance;
  }

  public load(assets: AssetsFile): void {
    this.words = assets.words;
    console.log(`Loaded ${Object.keys(this.words).length} words`);
  }

  public getWord(word: string): KeyedWord | undefined {
    const filteredWord = this.normalizeKey(word);

    const keyedWord = this.words?.[word] || this.words?.[filteredWord] || this.words?.[filteredWord.toLowerCase()];
    if (!keyedWord) {
      return undefined;
    }

    let appearance = this.handlePunctuation(word, keyedWord.appearance);
    appearance = this.handleCapitalization(word, appearance);

    return {
      key: word,
      ...keyedWord,
      appearance,
    };
  }

  public getWordAudioUrl(word: string): string {
    return this.assetFilesUtil.getS3Url(`audios/words/${this.normalizeKey(word)}`, 'mp3');
  }

  // Helper Methods
  private normalizeKey(word: string): string {
    return word.replace(/[^a-zA-Z'_]/g, '');
  }

  private handlePunctuation(word: string, appearance: string[]): string[] {
    const punctuationChars = [',', '.', '?', '"', '-', ':'];
    const newAppearance = [...appearance];

    const punctuationPositions = Array.from(word).reduce<{ char: string; index: number }[]>((acc, char, index) => {
      if (punctuationChars.includes(char)) {
        acc.push({ char, index });
      }
      return acc;
    }, []);

    let insertedPunctuationCount = 0;
    for (const { char, index } of punctuationPositions) {
      const actualIndex = index + insertedPunctuationCount;
      if (actualIndex <= newAppearance.length) {
        newAppearance.splice(actualIndex, 0, char);
        insertedPunctuationCount++;
      } else {
        newAppearance.push(char);
      }
    }

    return newAppearance;
  }

  private handleCapitalization(word: string, appearance: string[]): string[] {
    if (word[0]?.toUpperCase() === word[0] && appearance.length > 0) {
      const newAppearance = [...appearance];
      // The first "sound" may contain multiple characters, so we need to capitalize ONLY the first character
      newAppearance[0] = `${newAppearance[0][0].toUpperCase()}${newAppearance[0].slice(1)}`;
      return newAppearance;
    }
    return appearance;
  }
}

export const getWordsUtil = (): WordsUtil => WordsUtil.instance;
